import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  Alert,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';

import { formatFileSize, type UseDocumentUpload } from './useDocumentUpload';

interface DocumentUploadProgressProps {
  upload: UseDocumentUpload;
}

/**
 * The uploading / complete view: per-file status plus a summary. Shared by
 * DocumentUploadDialog and the Add-to-Property "AI pipeline" tab.
 */
const DocumentUploadProgress: React.FC<DocumentUploadProgressProps> = ({ upload }) => {
  const { fileEntries, currentUploadIndex, uploadComplete, successCount, errorCount } = upload;

  return (
    <>
      {!uploadComplete ? (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Uploading {currentUploadIndex + 1} of {fileEntries.length}...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={((currentUploadIndex + 1) / fileEntries.length) * 100}
            sx={{ mt: 2 }}
          />
        </Box>
      ) : (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Upload Complete
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {successCount} of {fileEntries.length} file{fileEntries.length !== 1 ? 's' : ''}{' '}
            uploaded successfully.
          </Typography>
          {errorCount > 0 ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {errorCount} file{errorCount !== 1 ? 's' : ''} failed to upload.
            </Typography>
          ) : null}
        </Box>
      )}

      <List dense>
        {fileEntries.map((entry, index) => (
          <ListItem key={`${entry.file.name}-${index}`}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {entry.status === 'success' ? (
                <CheckCircleOutlineIcon color="success" />
              ) : entry.status === 'error' ? (
                <ErrorOutlineIcon color="error" />
              ) : (
                <InsertDriveFileOutlinedIcon
                  color={entry.status === 'uploading' ? 'primary' : 'disabled'}
                />
              )}
            </ListItemIcon>
            <ListItemText
              primary={entry.file.name}
              secondary={entry.error ?? formatFileSize(entry.file.size)}
              secondaryTypographyProps={entry.error ? { color: 'error' } : undefined}
            />
          </ListItem>
        ))}
      </List>

      {errorCount > 0 && uploadComplete ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Some files failed to upload. Successfully uploaded files are being processed.
        </Alert>
      ) : null}
    </>
  );
};

export default DocumentUploadProgress;
